import { PdfTemplateTagInstance } from '~/src/models';
import { Project } from '~/src/models/project';
import { InputType } from '~/src/models/inputType';

/**
 * Converts pdfTemplateTagInstances into the allElements format that
 * useBackend.js expects, allowing the form template view to reuse the
 * same annotation rendering as the drafting preview.
 */
export function toDocumentElements(
  tagInstances: PdfTemplateTagInstance[],
  values: Record<string, string> | null,
): Project.DocumentElement[] {
  const valuesMap = values || {};

  return tagInstances.map((tag) => {
    const pdfElementID = tag.pdfElementID.toString();
    let value: string | null = valuesMap[pdfElementID] ?? '';

    if (
      tag.input_type_from_pdf === InputType.Checkbox ||
      tag.input_type_from_pdf === InputType.Button
    ) {
      value = valuesMap[pdfElementID] === 'Yes' ? 'Yes' : null;
    }

    return {
      id: tag.pdfElementID,
      inputType: tag.input_type_from_pdf as InputType,
      left: tag.left,
      top: tag.top,
      width: tag.width,
      height: tag.height,
      lineHeight: tag.lineHeight || null,
      pageNumber: tag.pageNumber,
      tag: null,
      aliasTag: null,
      value,
      computedFieldType: null,
      computedFieldTag: null,
      computedTagDestination: null,
      maxCharacters: null,
      isTaggedField: !!(tag.tagStr || tag.computedFieldTag),
    };
  });
}

/**
 * Normalizes a PDFTron field value for persistence.
 * Checkboxes ('Btn'): 'Yes' / 'On' -> 'Yes', anything else -> null (skip).
 * Text fields: non-empty strings pass through, empty -> null (skip).
 */
export function normalizeFieldValue(
  fieldType: string,
  value: unknown,
): string | null {
  if (fieldType === 'Btn') {
    return value === 'Yes' || value === 'On' ? 'Yes' : null;
  }
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Extracts current field values from WebViewer as { [pdfElementID]: value }.
 * useBackend stores pdfElementID as customData('elementId') on each widget.
 */
export function extractFieldValues(webviewerInstance: {
  Core?: any;
  docViewer?: any;
}): Record<string, string> {
  const values: Record<string, string> = {};
  const annotManager =
    webviewerInstance.Core?.documentViewer?.getAnnotationManager() ??
    webviewerInstance.docViewer?.getAnnotationManager();

  if (!annotManager) return values;

  annotManager.getFieldManager().forEachField((field: any) => {
    const widget = field.widgets?.[field.widgets.length - 1];
    if (!widget) return;

    const elementId = widget.getCustomData('elementId');
    if (!elementId) return;

    const normalized = normalizeFieldValue(field.type, field.getValue());
    if (normalized !== null) {
      values[elementId] = normalized;
    }
  });

  return values;
}
